// Slots where the caret sits on a name the user is INVENTING (a routine
// parameter, a procedural local / cursor / handler, a JSON_TABLE column) or on
// a bare literal the grammar fixes (a partition count). Nothing the schema
// knows can go there: every table, view, column and data type the resolver
// could offer is either illegal or, worse, a name the declaration would
// shadow. Before this module every one of these carets fell to the `unknown`
// arm, where the resolver answered with the whole relation list.
//
// Sibling of `renameTargetSlot.ts` (the rename's new-name slot) and
// `savepointSlot.ts` (a name only the buffer declares). Same contract as
// both: the slot is CLOSED — it is claimed even when the vocabulary is empty,
// because an empty widget is the honest answer and the relation flood it
// replaces was never a legal completion. Where a small CLOSED keyword set IS
// legal at the slot (a MySQL parameter mode, a handler action, a nested
// JSON_TABLE path) it is emitted, and nothing else.
//
// Cross-engine notes (parity rule — each divergence is deliberate):
//   - `routine-param-name` / `declare-name` are MySQL-only. MySQL spells the
//     parameter mode BEFORE the name (`IN p_x INT`), so the mode keywords are
//     this slot's vocabulary; Oracle spells it AFTER (`p_x IN NUMBER`) and PG
//     allows a bare type with no name at all (`f(integer)`), so on those
//     engines a data-type/mode emission belongs to the slot AFTER the name and
//     claiming here would suppress a legal answer. T-SQL's `DECLARE ▮@x` is a
//     sigil slot its own resolver already handles.
//   - `partition-count` and `json-table-column` are shared by MySQL + Oracle,
//     the two engines whose grammar has `PARTITIONS <n>` and
//     `JSON_TABLE(… COLUMNS (…))`.

import type { CompletionCandidate, DatabaseEngineId } from '@/core/engines';

import { dropAttachedPartialTokens, tokenize, type Token } from './tokenizer';

export type DeclaredNameSlotKind =
  /** `CREATE PROCEDURE|FUNCTION p(▮` / `…, ▮` — a fresh parameter slot. */
  | 'routine-param-name'
  /** `DECLARE ▮` — a local, cursor or handler declaration's own name. */
  | 'declare-name'
  /** `PARTITIONS ▮` / `SUBPARTITIONS ▮` — an integer literal. */
  | 'partition-count'
  /** `JSON_TABLE(… COLUMNS (▮` / `…, ▮` — a fresh column definition. */
  | 'json-table-column';

export type DeclaredNameSlot = {
  readonly kind: DeclaredNameSlotKind;
  /** The CLOSED keyword vocabulary legal at this caret (may be empty). */
  readonly keywords: readonly string[];
};

/** MySQL stored-PROCEDURE parameter modes. A FUNCTION's parameters are always
 *  IN and MySQL rejects a mode keyword there, so the vocabulary is empty. */
const MYSQL_PARAM_MODES = ['IN', 'OUT', 'INOUT'] as const;

/** MySQL `DECLARE {CONTINUE | EXIT | UNDO} HANDLER FOR …` actions — the only
 *  words that may follow `DECLARE` other than the invented name itself. */
const MYSQL_HANDLER_ACTIONS = ['CONTINUE', 'EXIT', 'UNDO'] as const;

/** A JSON_TABLE COLUMNS entry may start a nested projection instead of naming
 *  a column. Spelled the same on MySQL and Oracle. */
const JSON_TABLE_ENTRY_KEYWORDS = ['NESTED PATH'] as const;

/**
 * Claim the caret when it is a declared-name / literal-only slot and emit the
 * slot's closed vocabulary. Returns true when claimed, so the caller
 * short-circuits its schema emission.
 */
export function emitDeclaredNameSlotCandidates(
  out: CompletionCandidate[],
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): boolean {
  const slot = detectDeclaredNameSlot(text, offset, statementStart, engine);
  if (!slot) return false;
  for (const kw of slot.keywords) {
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'SQL keyword',
    });
  }
  return true;
}

/**
 * The declared-name slot the caret sits in, or null. A partially typed name
 * still counts — the slot must stay claimed while the user types, or the
 * relation list reappears on the first keystroke.
 */
export function detectDeclaredNameSlot(
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): DeclaredNameSlot | null {
  if (engine !== 'mysql' && engine !== 'oracle') return null;
  const tokens = dropAttachedPartialTokens(
    tokenize(text, statementStart, offset, engine),
    offset,
  );
  if (tokens.length === 0) return null;

  const tail = tokens[tokens.length - 1]!;
  const tailWord = wordOf(tail);

  if (tailWord === 'PARTITIONS' || tailWord === 'SUBPARTITIONS') {
    return { kind: 'partition-count', keywords: [] };
  }
  if (engine === 'mysql' && tailWord === 'DECLARE') {
    return { kind: 'declare-name', keywords: MYSQL_HANDLER_ACTIONS };
  }

  // The remaining two shapes are fresh entries in an unclosed list — the caret
  // sits directly after the opener or a separator.
  if (tail.type !== 'lparen' && tail.type !== 'comma') return null;
  const lparenIdx = enclosingLparenIndex(tokens);
  if (lparenIdx < 0) return null;

  if (isJsonTableColumnsOpener(tokens, lparenIdx)) {
    return { kind: 'json-table-column', keywords: JSON_TABLE_ENTRY_KEYWORDS };
  }
  if (engine === 'mysql') {
    const routine = mysqlRoutineParamListHead(tokens, lparenIdx);
    if (routine === 'procedure') {
      return { kind: 'routine-param-name', keywords: MYSQL_PARAM_MODES };
    }
    if (routine === 'function') {
      return { kind: 'routine-param-name', keywords: [] };
    }
  }
  return null;
}

/** Index of the innermost `(` still unclosed at the end of the stream, or -1. */
function enclosingLparenIndex(tokens: readonly Token[]): number {
  const stack: number[] = [];
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokens[i]!.type === 'lparen') stack.push(i);
    else if (tokens[i]!.type === 'rparen') stack.pop();
  }
  return stack.length > 0 ? stack[stack.length - 1]! : -1;
}

/**
 * True when the unclosed `(` at `lparenIdx` opens a `JSON_TABLE(… COLUMNS (`
 * projection list. The opener's own preceding word must be `COLUMNS`, and the
 * paren that CONTAINS it must belong to `JSON_TABLE` (or to another `COLUMNS`
 * list — the `NESTED PATH … COLUMNS (` nesting). The containing-call check is
 * what keeps Oracle's `GRAPH_TABLE(… COLUMNS (▮` out: that slot names graph
 * element properties, a real expression position the graph resolver answers.
 */
function isJsonTableColumnsOpener(
  tokens: readonly Token[],
  lparenIdx: number,
): boolean {
  if (wordOf(tokens[lparenIdx - 1]) !== 'COLUMNS') return false;
  const parentIdx = enclosingLparenIndex(tokens.slice(0, lparenIdx));
  if (parentIdx < 0) return false;
  const owner = wordOf(tokens[parentIdx - 1]);
  return owner === 'JSON_TABLE' || owner === 'COLUMNS';
}

/**
 * `'procedure'` / `'function'` when the unclosed `(` at `lparenIdx` is the
 * parameter list of a `CREATE [OR REPLACE] PROCEDURE|FUNCTION <name> (` head,
 * else null. `DELIMITER //` may lead the statement window — MySQL's client
 * directive is not a statement separator the boundary scanner knows, so it
 * rides along and is skipped here (same rule as
 * `editorContextSlot.mysql.ts`'s param-list head walk).
 */
function mysqlRoutineParamListHead(
  tokens: readonly Token[],
  lparenIdx: number,
): 'procedure' | 'function' | null {
  const head = tokens
    .slice(0, lparenIdx)
    .map((t) => wordOf(t))
    .filter((w): w is string => w !== null);
  let i = 0;
  while (head[i] === 'DELIMITER') {
    i += 1;
    if (head[i] !== undefined && head[i] !== 'CREATE') i += 1;
  }
  if (head[i] !== 'CREATE') return null;
  i += 1;
  if (head[i] === 'OR') {
    i += 1;
    if (head[i] !== 'REPLACE') return null;
    i += 1;
  }
  if (head[i] === 'PROCEDURE') return 'procedure';
  if (head[i] === 'FUNCTION') return 'function';
  return null;
}

function wordOf(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}
